import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Save, X, Loader2, DollarSign, Star } from "lucide-react";

const PricingManager = () => {
  const { authAxios } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    price: "",
    duration: "Monthly",
    features: "",
    is_popular: false
  });

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/pricing`);
      setPlans(response.data);
    } catch (e) {
      toast.error("Failed to fetch plans");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: formData.name,
        price: parseFloat(formData.price),
        duration: formData.duration,
        features: formData.features.split('\n').filter(f => f.trim()),
        is_popular: formData.is_popular
      };
      
      await authAxios.post('/pricing', payload);
      toast.success("Plan added successfully!");
      setShowForm(false);
      resetForm();
      fetchPlans();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add plan");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this pricing plan?")) return;
    
    try {
      await authAxios.delete(`/pricing/${id}`);
      toast.success("Plan deleted");
      fetchPlans();
    } catch (e) {
      toast.error("Failed to delete plan");
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      price: "",
      duration: "Monthly",
      features: "",
      is_popular: false
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 text-neon-green animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="font-orbitron text-xl text-white flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-neon-green" />
          Pricing Plans ({plans.length})
        </h2>
        <button
          onClick={() => setShowForm(!showForm)}
          data-testid="add-plan-btn"
          className="cyber-btn py-2 px-4 text-xs border-neon-green text-neon-green hover:bg-neon-green hover:text-black"
        >
          <span className="flex items-center gap-2">
            {showForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            {showForm ? "CANCEL" : "ADD PLAN"}
          </span>
        </button>
      </div>

      {/* Add Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="cyber-card p-6 space-y-4 border-neon-green/30" data-testid="plan-form">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="font-orbitron text-xs text-gray-400">Plan Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
                placeholder="e.g., PRO"
                className="bg-void-black border-white/10"
                data-testid="plan-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-orbitron text-xs text-gray-400">Price (USD) *</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.price}
                onChange={(e) => setFormData({...formData, price: e.target.value})}
                required
                placeholder="99.00"
                className="bg-void-black border-white/10"
                data-testid="plan-price-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-orbitron text-xs text-gray-400">Duration *</Label>
              <Input
                value={formData.duration}
                onChange={(e) => setFormData({...formData, duration: e.target.value})}
                required
                placeholder="Monthly"
                className="bg-void-black border-white/10"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="font-orbitron text-xs text-gray-400">Features (one per line) *</Label>
            <Textarea
              value={formData.features}
              onChange={(e) => setFormData({...formData, features: e.target.value})}
              required
              placeholder="Feature 1&#10;Feature 2&#10;Feature 3"
              rows={4}
              className="bg-void-black border-white/10 font-rajdhani"
              data-testid="plan-features-input"
            />
          </div>
          <div className="flex items-center gap-3">
            <Switch
              checked={formData.is_popular}
              onCheckedChange={(v) => setFormData({...formData, is_popular: v})}
            />
            <Label className="font-rajdhani text-sm text-gray-400">Mark as Popular</Label>
          </div>
          <div className="flex justify-end">
            <button type="submit" className="cyber-btn py-2 px-6 text-xs border-neon-green text-neon-green hover:bg-neon-green hover:text-black" data-testid="save-plan-btn">
              <span className="flex items-center gap-2">
                <Save className="w-4 h-4" />
                SAVE PLAN
              </span>
            </button>
          </div>
        </form>
      )}

      {/* Plans List */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {plans.map(plan => (
          <div key={plan.id} className={`cyber-card p-6 ${plan.is_popular ? 'border-2 border-neon-pink' : ''}`} data-testid={`plan-item-${plan.id}`}>
            {plan.is_popular && (
              <div className="flex items-center gap-1 text-neon-pink text-xs font-orbitron mb-2">
                <Star className="w-3 h-3 fill-current" />
                POPULAR
              </div>
            )}
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-orbitron text-lg text-white mb-1">{plan.name}</h3>
                <p className="font-rajdhani text-xs text-gray-500 mb-3">{plan.duration}</p>
              </div>
              <button
                onClick={() => handleDelete(plan.id)}
                className="text-gray-500 hover:text-red-500 transition-colors p-1"
                data-testid={`delete-plan-${plan.id}`}
                title="Delete plan"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            <div className="font-orbitron text-3xl text-neon-green mb-4">
              ${plan.price.toFixed(0)}
            </div>
            <ul className="space-y-2 mb-4">
              {plan.features.map((feature, i) => (
                <li key={i} className="font-rajdhani text-xs text-gray-400 flex items-start gap-2">
                  <span className="text-neon-blue">✓</span>
                  {feature}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {plans.length === 0 && (
        <div className="text-center py-12 cyber-card" data-testid="empty-plans-state">
          <DollarSign className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <p className="font-rajdhani text-gray-500">No pricing plans yet. Add your first plan!</p>
        </div>
      )}
    </div>
  );
};

export default PricingManager;
